import React, { useEffect, useState } from 'react';
import {
  Typography,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Box,
  styled,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { SimpleTreeView, TreeItem } from '@mui/x-tree-view';
import {
  GetTemplateCategory,
  GetTemplateItem,
  GetTemplateItemMaster,
  GetTemplateType,
} from '../../../services/apiTypes';
import { isRejected } from '@reduxjs/toolkit';
import useErrorModal from '../../../hooks/useErrorModal';
import { AppDispatch } from '../../../stores/store';
import { useDispatch } from 'react-redux';
import { updateTemplateItemMaster } from '../../../stores/templateItem/slice';
import { API_CODE } from '../../../_constants/apiConst';
import { TEXT_OVERFLOW_1LINE } from '../../../_constants/styleConst';

type EditTemplateItemsModalProps = {
  isOpen: boolean;
  templateId: number;
  isReadOnly: boolean;
  templateItemMaster: GetTemplateItemMaster;
  handleClose: (isSave: boolean) => void;
};

/** 階層に応じて右にずらす */
const StyledTreeItem = styled(TreeItem)(() => ({
  '& .MuiTreeItem-content': {
    display: 'flex',
    alignItems: 'center',
    marginLeft: `calc(var(--TreeView-itemDepth) * 25px)`,
    // ダイアログのwidth(sm=600px) - 左右のpadding(24px*2) - marginLeft
    maxWidth: `calc(600px - (24px * 2) - (var(--TreeView-itemDepth) * 25px))`,
  },
}));

/** ツリーの1行分のフォーム */
const StyledFormControlLabel = styled(FormControlLabel)(() => ({
  marginLeft: 0,
  marginRight: 0,
  '& .MuiFormControlLabel-label': {
    ...TEXT_OVERFLOW_1LINE,
    color: 'black',
  },
  '&.Mui-disabled': {
    color: 'rgba(0,0,0,0.38)!important',
  },
}));

/** チェックボックス */
const StyledCheckbox = styled(Checkbox)(() => ({
  marginRight: '10px',
  padding: 0,
}));

/** テンプレートの項目ダイアログ */
const TemplateItemsModal: React.FC<EditTemplateItemsModalProps> = ({
  isOpen,
  templateId,
  isReadOnly,
  templateItemMaster,
  handleClose,
}) => {
  const { t } = useTranslation();
  const dispatch: AppDispatch = useDispatch();
  const { getApiErrorMessage } = useErrorModal();
  /** エラーメッセージ */
  const [errorMsg, setErrorMsg] = useState<string>('');
  /** 展開されているキー */
  const [expandedItems, setExpandedItems] = useState<string[]>([]);
  /** 編集中の項目マスタ */
  const [selectedItemSet, setSelectedItemSet] = useState<Set<string>>(
    new Set(),
  );
  /** 保存ボタンが非活性か */
  const [isDisabledSaveBtn, setIsDisabledSaveBtn] = useState<boolean>(false);

  useEffect(() => {
    // 選択状態の初期化
    if (isOpen) {
      const defaultItemSet: Set<string> = new Set();
      templateItemMaster.forEach((category) =>
        category.typeList.forEach((type) => {
          type.itemList.forEach((depth0) => {
            if (depth0.isSelect) {
              defaultItemSet.add(depth0.itemId);
            }
            depth0.itemList.forEach((depth1) => {
              if (depth1.isSelect) {
                defaultItemSet.add(depth1.itemId);
              }
              depth1.itemList.forEach((depth2) => {
                if (depth2.isSelect) {
                  defaultItemSet.add(depth2.itemId);
                }
              });
            });
          });
        }),
      );
      setSelectedItemSet(defaultItemSet);
    }
  }, [isOpen]);

  /** 展開状態の変更 */
  const handleExpandedItemsChange = (
    _: React.SyntheticEvent | null,
    itemIds: string[],
  ) => {
    setExpandedItems(itemIds);
  };

  /** 大項目の全て展開ボタン */
  const onClickExpand = (category: GetTemplateCategory) => {
    setExpandedItems((prev) => {
      const newItems = [...prev, category.categoryId];
      category.typeList.forEach((type) => {
        newItems.push(type.typeId);
        type.itemList.forEach((depth0) => {
          if (depth0.itemList.length > 0) {
            newItems.push(depth0.itemId);
            depth0.itemList.forEach((depth1) => {
              if (depth0.itemList.length > 0) {
                newItems.push(depth1.itemId);
              }
            });
          }
        });
      });
      return newItems;
    });
  };

  /** 大項目の全て閉じるボタン */
  const onClickClose = (category: GetTemplateCategory) => {
    setExpandedItems((prev) => {
      const closeList: string[] = [category.categoryId];
      category.typeList.forEach((type) => {
        closeList.push(type.typeId);
        type.itemList.forEach((depth0) => {
          if (depth0.itemList.length > 0) {
            closeList.push(depth0.itemId);
            depth0.itemList.forEach((depth1) => {
              if (depth0.itemList.length > 0) {
                closeList.push(depth1.itemId);
              }
            });
          }
        });
      });
      return prev.filter((item) => !closeList.includes(item));
    });
  };

  /** itemListを深ぼってitemIdをflatな配列にして返す */
  const getItemIdList: (itemList: GetTemplateItem[]) => string[] = (
    itemList: GetTemplateItem[],
  ) => {
    // flatMap()を使って再帰的に展開する
    return itemList.flatMap((item) => [
      item.itemId,
      ...getItemIdList(item.itemList),
    ]);
  };

  /** stateの更新処理を共通化 */
  const updateSelectedItemSet = (itemIdList: string[], isChecked: boolean) => {
    const newSet = new Set(selectedItemSet);
    // 全て選択 or 非選択にする
    itemIdList.forEach((itemId) => {
      if (isChecked) {
        newSet.add(itemId);
      } else {
        newSet.delete(itemId);
      }
    });

    setSelectedItemSet(newSet);
  };

  /** 大項目のチェックボックスの変更 */
  const onChangeCategoryCheck = (
    isChecked: boolean,
    typeList: GetTemplateType[],
  ) => {
    // 該当の大項目に含まれる小項目のIDリスト
    const itemIdList = typeList.flatMap((type) => getItemIdList(type.itemList));
    updateSelectedItemSet(itemIdList, isChecked);
  };

  /** 中項目のチェックボックスの変更 */
  const onChangeTypeCheck = (
    isChecked: boolean,
    itemList: GetTemplateItem[],
  ) => {
    // 該当の中項目に含まれる小項目のIDリスト
    const itemIdList = getItemIdList(itemList);
    updateSelectedItemSet(itemIdList, isChecked);
  };

  /** 小項目：階層0のチェックボックスの変更 */
  const onChangeDepth0Check = (isChecked: boolean, depth0: GetTemplateItem) => {
    // 自身と配下の小項目のIDリスト
    const itemIdList = [depth0.itemId, ...getItemIdList(depth0.itemList)];
    updateSelectedItemSet(itemIdList, isChecked);
  };

  /** 小項目：階層1のチェックボックスの変更 */
  const onChangeDepth1Check = (
    isChecked: boolean,
    depth0: GetTemplateItem,
    depth1: GetTemplateItem,
  ) => {
    // チェックの付け外しをするIDリスト(自身と子項目で初期化)
    const itemIdList: string[] = [
      depth1.itemId,
      ...getItemIdList(depth1.itemList),
    ];

    // 他の階層1にチェックが付いているか
    const isCheckedOther1 = depth0.itemList.some(
      (d1) => d1.itemId !== depth1.itemId && selectedItemSet.has(d1.itemId),
    );

    if (isChecked) {
      itemIdList.push(depth0.itemId);
    } else if (!isCheckedOther1) {
      // 階層0のチェックも外す
      itemIdList.push(depth0.itemId);
    }

    updateSelectedItemSet(itemIdList, isChecked);
  };

  /** 小項目：階層2のチェックボックスの変更 */
  const onChangeDepth2Check = (
    isChecked: boolean,
    depth0: GetTemplateItem,
    depth1: GetTemplateItem,
    depth2: GetTemplateItem,
  ) => {
    // チェックの付け外しをするIDリスト(自身のIDで初期化)
    const itemIdList: string[] = [depth2.itemId];

    // 他の階層2にチェックが付いているか
    const isCheckedOther2 = depth1.itemList.some(
      (d2) => d2.itemId !== depth2.itemId && selectedItemSet.has(d2.itemId),
    );
    // 他の階層1にチェックが付いているか
    const isCheckedOther1 = depth0.itemList.some(
      (d1) => d1.itemId !== depth1.itemId && selectedItemSet.has(d1.itemId),
    );

    if (isChecked) {
      itemIdList.push(depth0.itemId, depth1.itemId);
    } else if (!isCheckedOther2) {
      // 階層1のチェックも外す
      itemIdList.push(depth1.itemId);
      // 階層0のチェックも外す
      if (!isCheckedOther1) {
        itemIdList.push(depth0.itemId);
      }
    }

    updateSelectedItemSet(itemIdList, isChecked);
  };

  /** キャンセルボタン押下時 */
  const onClickCancel = () => {
    setErrorMsg('');
    setExpandedItems([]);
    handleClose(false);
  };

  /** 保存ボタン押下時 */
  const onClickSave = async () => {
    setErrorMsg('');
    // 連打防止でボタンを一時的に非活性にする
    setIsDisabledSaveBtn(true);
    // PUTの型に変換
    const newItems: { itemId: string; isSelect: boolean }[] = [];
    templateItemMaster.forEach((category) =>
      category.typeList.forEach((type) =>
        type.itemList.forEach((depth0) => {
          newItems.push({
            itemId: depth0.itemId,
            isSelect: selectedItemSet.has(depth0.itemId),
          });
          depth0.itemList.forEach((depth1) => {
            newItems.push({
              itemId: depth1.itemId,
              isSelect: selectedItemSet.has(depth1.itemId),
            });
            depth1.itemList.forEach((depth2) => {
              newItems.push({
                itemId: depth2.itemId,
                isSelect: selectedItemSet.has(depth2.itemId),
              });
            });
          });
        }),
      ),
    );
    const response = await dispatch(
      updateTemplateItemMaster({
        templateId: templateId,
        templateItemList: newItems,
      }),
    );
    setIsDisabledSaveBtn(false);
    if (isRejected(response)) {
      setErrorMsg(
        getApiErrorMessage(response.payload, API_CODE.TEMPLATE_ITEM_MASTER),
      );
      return;
    }
    handleClose(true);
  };

  const renderTree = () => {
    return templateItemMaster.map((category) => (
      // 大項目
      <StyledTreeItem
        key={category.categoryId}
        itemId={category.categoryId}
        sx={{
          color: category.typeList.every((type) => type.itemList.length === 0)
            ? 'rgba(0, 0, 0, 0.38)'
            : '',
        }}
        label={
          <Box display="flex" justifyContent="space-between">
            <StyledFormControlLabel
              // ボタン要素のwidth(190px)
              sx={{ width: 'calc(100% - 190px)' }}
              label={category.categoryName}
              disabled={category.typeList.every(
                (type) => type.itemList.length === 0,
              )}
              control={
                <StyledCheckbox
                  disabled={isReadOnly}
                  checked={category.typeList.some((type) =>
                    type.itemList.some((depth0) =>
                      selectedItemSet.has(depth0.itemId),
                    ),
                  )}
                  onChange={(_, checked) =>
                    onChangeCategoryCheck(checked, category.typeList)
                  }
                />
              }
            />
            <Box width="190px" display="flex" justifyContent="end">
              {/* 全て展開/閉じるボタン */}
              {category.typeList.length > 0 && (
                <React.Fragment>
                  <Button
                    sx={{ py: 0 }}
                    onClick={() => onClickExpand(category)}
                  >
                    {t('LBL_ALL_EXPAND')}
                  </Button>
                  <Button sx={{ py: 0 }} onClick={() => onClickClose(category)}>
                    {t('LBL_ALL_COLLAPSE')}
                  </Button>
                </React.Fragment>
              )}
            </Box>
          </Box>
        }
      >
        {category.typeList.map((type) => (
          // 中項目
          <StyledTreeItem
            key={type.typeId}
            itemId={type.typeId}
            disabled={type.itemList.length === 0}
            label={
              <StyledFormControlLabel
                label={type.typeName}
                control={
                  <StyledCheckbox
                    disabled={isReadOnly}
                    checked={type.itemList.some((depth0) =>
                      selectedItemSet.has(depth0.itemId),
                    )}
                    onChange={(_, checked) =>
                      onChangeTypeCheck(checked, type.itemList)
                    }
                  />
                }
              />
            }
          >
            {type.itemList.map((depth0) => (
              // 小項目：階層0
              <StyledTreeItem
                key={depth0.itemId}
                itemId={depth0.itemId}
                label={
                  <StyledFormControlLabel
                    label={depth0.itemName}
                    control={
                      <StyledCheckbox
                        disabled={isReadOnly}
                        checked={selectedItemSet.has(depth0.itemId)}
                        onChange={(_, checked) =>
                          onChangeDepth0Check(checked, depth0)
                        }
                      />
                    }
                  />
                }
              >
                {depth0.itemList.map((depth1) => (
                  // 小項目：階層1
                  <StyledTreeItem
                    key={depth1.itemId}
                    itemId={depth1.itemId}
                    label={
                      <StyledFormControlLabel
                        label={depth1.itemName}
                        control={
                          <StyledCheckbox
                            disabled={isReadOnly}
                            checked={selectedItemSet.has(depth1.itemId)}
                            onChange={(_, checked) =>
                              onChangeDepth1Check(checked, depth0, depth1)
                            }
                          />
                        }
                      />
                    }
                  >
                    {depth1.itemList.map((depth2) => (
                      // 小項目：階層2
                      <StyledTreeItem
                        key={depth2.itemId}
                        itemId={depth2.itemId}
                        label={
                          <StyledFormControlLabel
                            label={depth2.itemName}
                            control={
                              <StyledCheckbox
                                disabled={isReadOnly}
                                checked={selectedItemSet.has(depth2.itemId)}
                                onChange={(_, checked) =>
                                  onChangeDepth2Check(
                                    checked,
                                    depth0,
                                    depth1,
                                    depth2,
                                  )
                                }
                              />
                            }
                          />
                        }
                      ></StyledTreeItem>
                    ))}
                  </StyledTreeItem>
                ))}
              </StyledTreeItem>
            ))}
          </StyledTreeItem>
        ))}
      </StyledTreeItem>
    ));
  };

  return (
    <Dialog open={isOpen} fullWidth maxWidth="sm">
      <Box display="flex">
        <DialogTitle>{t('LBL_INSPECTION_ITEM_SETTING')}</DialogTitle>
        <Typography alignSelf="center" color="error">
          {errorMsg}
        </Typography>
      </Box>
      <DialogContent dividers>
        <SimpleTreeView
          expansionTrigger="iconContainer"
          expandedItems={expandedItems}
          onExpandedItemsChange={handleExpandedItemsChange}
        >
          {renderTree()}
        </SimpleTreeView>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={onClickCancel}
          color="inherit"
          variant="contained"
          sx={{ width: '120px', mr: 2, my: 1 }}
        >
          {t('BTN_CANCEL')}
        </Button>
        {!isReadOnly && (
          <Button
            color="primary"
            variant="contained"
            sx={{ width: '120px', mr: 3, my: 1 }}
            disabled={isDisabledSaveBtn}
            onClick={onClickSave}
          >
            {t('BTN_SAVE')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default TemplateItemsModal;
